import React from 'react';
import { motion } from 'framer-motion';
import { FiTarget, FiTrendingUp, FiCheckCircle, FiArrowRight, FiShoppingCart, FiEye, FiSearch, FiRefreshCw } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const GoogleAdsPage = () => {
  useDocumentTitle(
    'Google Ads Management in India | Led by a Skilled Google Ads Expert in India',
    'A ppc advertising agency that simplifies Google Ads for you. built by a dedicated google ads expert in india who focuses on quality traffic and scalable conversions.'
  );
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-orange-600 via-red-600 to-red-700">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>

        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="flex justify-center items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl text-center"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Google Ads Management That Actually Brings You Customers
              </h1>
              <div className="space-y-3 mb-8 text-white/90 text-base md:text-lg">
                <p>Looking for a <span className="font-semibold">google ads expert in india</span> who cares about profit, not just clicks?</p>
                <p>Tired of big promises from every <span className="font-semibold">ppc ad agency</span>?</p>
                <p>Work with a focused <span className="font-semibold">ppc advertising agency</span> that treats your money like its own and gives you clear numbers.</p>
              </div>
              <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
                Mirani Marketing runs Google Ads campaigns that find the right people, at the right time, with the right message. If you're comparing options for a <span className="font-semibold">ppc agency in india</span>, we make it simple, transparent and performance‑driven.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Get a Free Google Ads Audit</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
                >
                  <span>Talk to Our Google Ads Expert</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Google Ads */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is Google Ads?</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Google Ads is the platform that lets your business show up at the top of Google when people are already searching for your product or service. Instead of waiting for organic results, you can turn those searches into real leads, calls and sales with well‑planned campaigns and clean tracking.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiSearch className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Search Ads</h3>
              <p className="text-gray-600 leading-relaxed">
                Appear at the top of Google search results when customers search for keywords related to your business. Capture high-intent traffic ready to convert.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiEye className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Display Ads</h3>
              <p className="text-gray-600 leading-relaxed">
                Reach millions of users across 2 million+ websites with visually engaging banner ads that build brand awareness and drive consideration.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiShoppingCart className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Shopping Ads</h3>
              <p className="text-gray-600 leading-relaxed">
                Showcase your products with images, prices, and store information directly in Google search results, perfect for e-commerce businesses.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiRefreshCw className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Remarketing</h3>
              <p className="text-gray-600 leading-relaxed">
                Re-engage visitors who left your website without converting with targeted ads that bring them back to complete their purchase.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our Google Ads Process</h2>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {[
              {
                step: '01',
                title: 'Understand Your Business',
                description: 'Learn your goals, offers, margins, locations and ideal customers so we know what counts as a "good" lead for you.',
                icon: FiTarget
              },
              {
                step: '02',
                title: 'Research & Strategy',
                description: 'Find the real search terms your audience uses, study competitors and build a simple, clear plan for search, Performance Max or YouTube (only where it makes sense).',
                icon: FiSearch
              },
              {
                step: '03',
                title: 'Campaign & Tracking Setup',
                description: 'Create campaigns, ad groups, ads, extensions and conversion tracking so every important action is measured correctly.',
                icon: FiEye
              },
              {
                step: '04',
                title: 'Daily / Weekly Optimisation',
                description: 'Add and block keywords, adjust bids, test new ads and audiences, and cut what is wasting money. The goal is always better cost per lead or sale.',
                icon: FiTrendingUp
              },
              {
                step: '05',
                title: 'Reporting & Next Steps',
                description: 'Share easy-to-read reports: what we spent, what we got, what we\'re changing next. No jargon, just honest numbers.',
                icon: FiCheckCircle
              }
            ].map((process, index) => (
              <div key={index} className="flex gap-6 items-start group">
                <div className="flex-shrink-0">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white text-2xl font-bold w-16 h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">
                    {process.step}
                  </div>
                </div>
                <div className="flex-grow bg-gray-50 rounded-xl p-6 group-hover:shadow-xl transition-all duration-300">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Google Ads Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible pricing based on your advertising budget and goals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Starter',
                price: '₹20,000',
                duration: '/month',
                popular: false,
                features: [
                  'Ad Spend: Up to ₹50,000',
                  'Search Campaigns',
                  'Basic Display Ads',
                  'Monthly Reports',
                  'Conversion Tracking',
                  'Email Support'
                ]
              },
              {
                name: 'Growth',
                price: '₹40,000',
                duration: '/month',
                popular: true,
                features: [
                  'Ad Spend: Up to ₹1,50,000',
                  'Search + Display',
                  'Shopping Ads',
                  'Remarketing Campaigns',
                  'Bi-Weekly Reports',
                  'A/B Testing',
                  'Priority Support',
                  'Landing Page Advice'
                ]
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                duration: 'pricing',
                popular: false,
                features: [
                  'Unlimited Ad Spend',
                  'All Campaign Types',
                  'Video Ads',
                  'Advanced Remarketing',
                  'Weekly Reports',
                  'Dedicated Manager',
                  'Custom Strategy',
                  '24/7 Support'
                ]
              }
            ].map((pkg, index) => (
              <div 
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  pkg.popular ? 'ring-4 ring-orange-500 shadow-2xl' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{pkg.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{pkg.price}</span>
                  <span className="text-gray-600 ml-2">{pkg.duration}</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <FiCheckCircle className="text-orange-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Mirani as Your PPC Partner?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Performance‑first approach</h3>
              <p className="text-gray-600 leading-relaxed">
                Mirani Marketing works like a specialist ppc ad agency: every decision is driven by data, leads and revenue, not just impressions or clicks.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTarget className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Focused on paid search</h3>
              <p className="text-gray-600 leading-relaxed">
                Your account is handled by people who work on Google Ads every day, giving you the attention you'd expect from a serious ppc advertising agency without the big‑agency layers.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiCheckCircle className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Clear, honest communication</h3>
              <p className="text-gray-600 leading-relaxed">
                You know exactly how much you're spending, where it's going and what you're getting back. That's why many brands see us as their trusted ppc agency in india for long‑term growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about Google Ads services
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "How fast can I see results?",
                answer: "Most accounts start showing useful data and early wins within 2–4 weeks. Strong, stable performance normally comes after a few testing cycles."
              },
              {
                question: "What budget should I start with?",
                answer: "It depends on your industry and location. We'll suggest a realistic starting range that allows proper testing, then scale only when campaigns prove they can bring leads or sales at a good cost."
              },
              {
                question: "Can you work on my existing Google Ads account?",
                answer: "Yes. We begin with an audit, fix tracking and structure, then rebuild or clean up campaigns so they're easier to optimise."
              },
              {
                question: "Do you handle landing pages too?",
                answer: "We can give clear suggestions and simple changes to improve conversion rates, or work with your designer/team to make the page more ad-friendly."
              },
              {
                question: "Will I keep control of my account?",
                answer: "Yes. The Google Ads account is always yours. You have full access and can see every campaign and change we make."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-orange-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-orange-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Grow with Google Ads?</h2>
          <p className="text-2xl mb-12 text-white/90 max-w-3xl mx-auto">
            If you're looking for a google ads expert in india or comparing different ppc ad agency options, let Mirani Marketing review your account and show you what's possible with clean, honest Google Ads management.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
            >
              <span>Book a Free Strategy Call</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
            >
              <span>Get Your Google Ads Audit</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GoogleAdsPage;
